import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, RefreshCw, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { cn } from '../lib/utils';

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  title: string;
  mode?: 'user' | 'environment';
}

export function CameraModal({
  isOpen,
  onClose,
  onCapture,
  title,
  mode = 'environment',
}: CameraModalProps) {
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(true);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const isMirror = mode === 'user';

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setIsStarting(true);
    setError(null);
    stopCamera();

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: mode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = newStream;
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError(
        'Não foi possível acessar a câmera. Verifique as permissões do seu navegador.'
      );
    } finally {
      setIsStarting(false);
    }
  }, [mode, stopCamera]);

  useEffect(() => {
    if (isOpen && !capturedImage) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, capturedImage, startCamera, stopCamera]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Set canvas dimensions to match video stream
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Apply mirroring if active
    if (isMirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const handleRetake = () => {
    setCapturedImage(null);
    startCamera();
  };

  const handleConfirm = () => {
    if (!capturedImage) return;

    // Convert dataUrl to File
    fetch(capturedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `capture-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        onCapture(file);
        onClose();
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setCapturedImage(null);
        onClose();
      }}
      title={title}
      size="lg"
      className="!p-0" // Remove padding for the camera view
    >
      <div className="relative bg-slate-950 aspect-video md:aspect-[4/3] flex items-center justify-center overflow-hidden">
        {isStarting && !error && (
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">Iniciando câmera...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 text-center px-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <p className="text-white font-medium">{error}</p>
            <Button variant="ghost" onClick={startCamera}>
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Video stream */}
        {!capturedImage && !error && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              'w-full h-full object-cover',
              isMirror && 'scale-x-[-1]'
            )}
          />
        )}

        {/* Captured Preview */}
        {capturedImage && (
          <img
            src={capturedImage}
            alt="Captura"
            className="w-full h-full object-cover"
          />
        )}

        {/* Framing Overlay (Subtle) */}
        {!capturedImage && !error && !isStarting && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-4/5 h-4/5 border-2 border-white/20 rounded-xl border-dashed" />
            {mode === 'user' && (
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-64 border-2 border-white/40 rounded-[100px]" />
            )}
          </div>
        )}

        {/* Canvas for capture (hidden) */}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="p-6 bg-slate-50 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => {
            setCapturedImage(null);
            onClose();
          }}
        >
          Cancelar
        </Button>

        <div className="flex items-center gap-3">
          {capturedImage ? (
            <>
              <Button variant="outline" onClick={handleRetake}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Repetir
              </Button>
              <Button variant="primary" onClick={handleConfirm}>
                <Check className="w-4 h-4 mr-2" />
                Confirmar
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="lg"
              disabled={isStarting || !!error}
              onClick={handleCapture}
              className="rounded-full w-14 h-14 !p-0 shadow-lg shadow-primary-500/20"
            >
              <Camera className="w-6 h-6" />
            </Button>
          )}
        </div>

        <div className="w-[88px]" /> {/* Spacer to center the camera button */}
      </div>
    </Modal>
  );
}
